import { useCallback, useEffect, useState } from 'react';
import {
  buildAccessTimeline,
  buildLegacyPackAccessTimeline,
  buildManualMonthGrant,
  fetchClientAccess,
  grantClientAccess,
} from '../services/clientAccessManager';

function dateLabel(value) {
  if (!value || !Number.isFinite(Date.parse(value))) return '—';
  return new Date(value).toLocaleDateString('fr-CH', { day: '2-digit', month: 'long', year: 'numeric' });
}

export default function ClientAccessManager({ client }) {
  const [snapshot, setSnapshot] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      const result = await fetchClientAccess(client);
      if (result?.ok !== true || result?.contract_version !== 1) throw new Error('Contrat d’accès illisible');
      setSnapshot(result);
      setStatus('ready');
    } catch (caught) {
      setStatus('error');
      setError(caught?.message || 'Accès application indisponible');
    }
  }, [client]);

  useEffect(() => { void load(); }, [load]);

  const offerMonth = async () => {
    const accessEnd = snapshot?.access?.ends_at;
    const startsAt = accessEnd && Date.parse(accessEnd) > Date.now()
      ? new Date(accessEnd).toISOString()
      : new Date().toISOString();
    if (!window.confirm(`Offrir un mois d’application autonome à partir du ${dateLabel(startsAt)} ?`)) return;
    setStatus('saving');
    setError(null);
    try {
      await grantClientAccess(client, buildManualMonthGrant(startsAt));
      await load();
    } catch (caught) {
      setStatus('error');
      setError(caught?.message || 'Le mois offert n’a pas été enregistré');
    }
  };

  const access = snapshot?.access;
  const timeline = buildAccessTimeline(access) || buildLegacyPackAccessTimeline(client, access);
  return (
    <section className="jrn-cockpit-card jrn-client-access" aria-labelledby="client-access-title">
      <div className="jrn-cockpit-card__head">
        <span className="jrn-cockpit-card__eyebrow">Accès application</span>
        {access && <span className={`jrn-status-chip jrn-status-chip--${access.level === 'expired' ? 'warn' : 'done'}`}>{access.state === 'grace' ? 'Grâce' : access.level === 'expired' ? 'Terminé' : 'Actif'}</span>}
      </div>
      <h3 id="client-access-title" className="jrn-cockpit-card__title">Autonomie et abonnement</h3>
      {status === 'loading' && <p className="jrn-cockpit-card__priority-reason">Lecture de l’accès…</p>}
      {access && (
        <p className="jrn-cockpit-card__priority-reason">
          {access.level === 'followup' ? 'Suivi complet' : access.level === 'self_service' ? 'Application autonome' : 'Accès au contenu terminé'}
          {access.ends_at ? ` · jusqu’au ${dateLabel(access.ends_at)}` : ''}
          {access.grace_until ? ` · grâce jusqu’au ${dateLabel(access.grace_until)}` : ''}
        </p>
      )}
      {timeline && (
        <div className={`jrn-access-timeline jrn-access-timeline--${timeline.state}`}>
          <div className="jrn-access-timeline__head">
            <strong>{timeline.summary}</strong>
            <span>{Math.round(timeline.progress)} %</span>
          </div>
          <div
            className="jrn-access-timeline__track"
            role="progressbar"
            aria-label="Progression de l’accès application"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow={Math.round(timeline.progress)}
          >
            <span className="jrn-access-timeline__fill" style={{ width: `${timeline.progress}%` }} />
          </div>
          <div className="jrn-access-timeline__dates">
            <span><strong>{timeline.start_label}</strong>{dateLabel(timeline.segment_start)}</span>
            <span><strong>{timeline.end_label}</strong>{dateLabel(timeline.segment_end)}</span>
          </div>
          {timeline.after_label && (
            <p className="jrn-access-timeline__after">
              {timeline.after_at
                ? `${timeline.after_label}, jusqu’au ${dateLabel(timeline.after_at)}.`
                : timeline.after_label}
            </p>
          )}
        </div>
      )}
      <p className="jrn-cockpit-card__priority-reason">
        Le mois offert ouvre le programme, les recettes, le Meal Prep et les analyses de menus, sans messagerie ni suivi humain.
      </p>
      {error && <p role="alert" className="jrn-error">{error}</p>}
      <div className="jrn-actions">
        <button type="button" className="jrn-btn jrn-btn--secondary" onClick={offerMonth} disabled={status === 'saving'}>
          {status === 'saving' ? 'Enregistrement…' : 'Offrir 1 mois à la suite'}
        </button>
        <button type="button" className="jrn-btn jrn-btn--ghost" onClick={load} disabled={status === 'loading' || status === 'saving'}>
          Actualiser
        </button>
      </div>
    </section>
  );
}
